import { fetchObligationArc } from '@/api/content-api';
import { useNavigateWithContentFlush } from '@/hooks';
import { cn } from '@/lib/utils';
import { toContentHref } from '@/utils/content-ref';
import type { ObligationTimelineEntry } from '@vivport/backend/types';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowRight,
  ArrowRightLeft,
  CalendarClock,
  CheckCircle2,
  HelpCircle,
  Loader2,
  type LucideIcon,
  MessageCircle,
  Plus,
  RefreshCw,
  RotateCcw,
  Share2,
  Unlink,
  XCircle,
} from 'lucide-react';

type ArcEventType = ObligationTimelineEntry['eventType'];

const EVENT_META: Record<ArcEventType, { icon: LucideIcon; label: string }> = {
  CREATED: { icon: Plus, label: 'Created' },
  CLARIFICATION_REQUESTED: { icon: HelpCircle, label: 'Asked for clarification' },
  CLARIFICATION_ANSWERED: { icon: MessageCircle, label: 'Clarification answered' },
  REASSIGNED: { icon: ArrowRightLeft, label: 'Reassigned' },
  DELEGATED: { icon: Share2, label: 'Delegated' },
  STATUS_CHANGED: { icon: RefreshCw, label: 'Status changed' },
  COMPLETED: { icon: CheckCircle2, label: 'Completed' },
  REOPENED: { icon: RotateCcw, label: 'Reopened' },
  DUE_CHANGED: { icon: CalendarClock, label: 'Due date changed' },
  CANCELLED: { icon: XCircle, label: 'Cancelled' },
  DETACHED: { icon: Unlink, label: 'Detached from message' },
};

// Display-only formatting of a backend-computed duration; never derives a metric.
function formatDuration(ms: number): string {
  if (ms < 60_000) return '<1m';
  const minutes = Math.floor(ms / 60_000);
  const days = Math.floor(minutes / (60 * 24));
  const hours = Math.floor((minutes % (60 * 24)) / 60);
  const mins = minutes % 60;
  if (days > 0) return hours > 0 ? `${days}d ${hours}h` : `${days}d`;
  if (hours > 0) return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  return `${mins}m`;
}

interface ObligationTimelineProps {
  extractId: number;
  enabled: boolean;
}

export function ObligationTimeline({ extractId, enabled }: ObligationTimelineProps) {
  const navigate = useNavigateWithContentFlush();
  const arcQuery = useQuery({
    queryKey: ['obligation-arc', extractId],
    queryFn: () => fetchObligationArc(extractId),
    enabled,
  });

  if (arcQuery.isPending) {
    return (
      <div className="flex items-center gap-2 px-2 py-3 text-xs text-muted-foreground">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Loading history…
      </div>
    );
  }

  if (arcQuery.isError) {
    return (
      <div className="px-2 py-3 text-xs text-destructive">Could not load history.</div>
    );
  }

  const { timeline, metrics } = arcQuery.data;

  if (timeline.length === 0) {
    return <div className="px-2 py-3 text-xs italic text-muted-foreground">No history yet.</div>;
  }

  return (
    <div className="space-y-3 rounded-md border bg-muted/30 p-3">
      <div className="flex flex-col gap-1 text-xs text-muted-foreground">
        {metrics.whoseCourt.length > 0 ? (
          <p>
            <span className="font-medium text-foreground">Time in court:</span>{' '}
            {metrics.whoseCourt
              .map((span) => `${span.name} ${formatDuration(span.totalDurationMs)}`)
              .join(' · ')}
          </p>
        ) : null}
        {metrics.whoCompleted ? (
          <p>
            <span className="font-medium text-foreground">Completed by</span>{' '}
            {metrics.whoCompleted.name}
            {metrics.whoCompleted.reopenedAfter ? ' (later reopened)' : ''}
          </p>
        ) : null}
        {metrics.reopenCount > 0 ? <p>Reopened {metrics.reopenCount}×</p> : null}
        {metrics.clarificationCount > 0 ? (
          <p>In clarification {formatDuration(metrics.timeInClarificationMs)}</p>
        ) : null}
      </div>

      <ol className="space-y-3">
        {timeline.map((entry) => {
          const meta = EVENT_META[entry.eventType];
          const Icon = meta.icon;
          const showCourt = entry.toActioneePartyId !== null;
          const showStatus = entry.toStatus !== null;
          return (
            <li key={entry.sequence} className="flex gap-2 text-xs">
              <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
              <div className="min-w-0 flex-1 space-y-1">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <span className="font-medium text-foreground">{meta.label}</span>
                  <span className="text-muted-foreground">{entry.actorName ?? 'System'}</span>
                  <span className="text-muted-foreground">
                    {new Date(entry.occurredAt).toLocaleString()}
                  </span>
                </div>

                {showCourt ? (
                  <div className="flex items-center gap-1.5">
                    <span className="text-muted-foreground">Ball:</span>
                    {entry.fromActioneeName ? (
                      <span className="rounded bg-background px-1.5 py-0.5 text-muted-foreground">
                        {entry.fromActioneeName}
                      </span>
                    ) : (
                      <span className="px-0.5 text-muted-foreground">Unassigned</span>
                    )}
                    <ArrowRight className="h-3 w-3 text-muted-foreground" />
                    <span
                      className={cn(
                        'rounded px-1.5 py-0.5 font-medium',
                        'bg-highlight text-highlight-foreground'
                      )}
                    >
                      {entry.toActioneeName ?? 'Unassigned'}
                    </span>
                  </div>
                ) : null}

                {showStatus ? (
                  <p className="text-muted-foreground">
                    Status: {entry.fromStatus ?? '—'} → <span className="text-foreground">{entry.toStatus}</span>
                  </p>
                ) : null}

                {entry.toDueAt !== null || entry.fromDueAt !== null ? (
                  entry.eventType === 'DUE_CHANGED' ? (
                    <p className="text-muted-foreground">
                      Due: {entry.fromDueAt ? new Date(entry.fromDueAt).toLocaleDateString() : 'none'} →{' '}
                      {entry.toDueAt ? new Date(entry.toDueAt).toLocaleDateString() : 'none'}
                    </p>
                  ) : null
                ) : null}

                {entry.highlightQuote ? (
                  <blockquote className="border-l-2 border-muted-foreground/30 pl-2 italic text-muted-foreground">
                    “{entry.highlightQuote}”
                  </blockquote>
                ) : null}

                {entry.note ? <p className="text-muted-foreground">{entry.note}</p> : null}

                {entry.sourceContentCode ? (
                  <button
                    type="button"
                    className="text-primary underline-offset-2 hover:underline"
                    onClick={() => navigate(toContentHref(entry.sourceContentCode as string))}
                  >
                    {entry.sourceContentCode}
                  </button>
                ) : null}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
